import {Dialog, Heading, Modal, ModalOverlay} from 'react-aria-components'

import AddressFormDivider from '@/assets/icons/address-form-divider.svg'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import {useAddresses} from '@/lib/payment/hooks/useAddresses'
import {useAddressOperations} from '@/lib/payment/hooks/useAddressOperations'

import style from './AddressDialog.module.scss'
import type {AddressFormHandle} from './AddressForm'
import AddressForm from './AddressForm'

interface AddressDialogProps {
  title: string
  editIndex?: number
  onClose: () => void
}

export const AddressDialog = memo<AddressDialogProps>(function AddressDialog({
  title,
  editIndex,
  onClose,
}) {
  const formRef = useRef<AddressFormHandle>(null)

  const {data: addresses} = useAddresses()
  const {mutation, addAddress, editAddress} = useAddressOperations()

  const initialAddress = editIndex !== undefined ? addresses?.shippingInfos?.[editIndex] : undefined
  const isEditMode = editIndex !== undefined

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!formRef.current) return false
    const result = await formRef.current.submit()
    if (!result.isValid) return false

    if (isEditMode) {
      editAddress(editIndex, result.data, result.makeDefault)
    } else {
      addAddress(result.data, result.makeDefault)
    }

    return true
  }

  const handleClose = (closeFunc: () => void) => {
    closeFunc()
    onClose()
  }

  const prevIsSuccess = useRef(mutation.isSuccess)

  useEffect(() => {
    if (mutation.isSuccess && !prevIsSuccess.current) {
      onClose()
    }
    prevIsSuccess.current = mutation.isSuccess
  }, [mutation.isSuccess, onClose])

  // Fix: react-aria v1.12.2 blocks Google Places autocomplete by setting 'inert' on external elements
  useEffect(() => {
    const removeInert = () =>
      { document.querySelectorAll('.pac-container[inert]').forEach(el => { el.removeAttribute('inert'); }); }

    const observer = new MutationObserver(removeInert)
    observer.observe(document.body, {attributes: true, subtree: true, attributeFilter: ['inert']})
    return () => { observer.disconnect(); }
  }, [])

  return (
    <ModalOverlay
      isDismissable
      shouldCloseOnInteractOutside={() => false}
      isKeyboardDismissDisabled
      className={style.Overlay}
    >
      <Modal className={style.Modal}>
        <Dialog className={style.Dialog} aria-labelledby='dialog-title'>
          {({close}) => (
            <>
              <div className={style.DialogHeader}>
                <Heading id='dialog-title'>{title}</Heading>
                <button
                  type='button'
                  onClick={() => {
                    handleClose(close)
                  }}
                  className={style.CloseButton}
                  aria-label='Close dialog'
                >
                  ×
                </button>
                <div className={style.Divider}>
                  <img
                    className={style.DividerImage}
                    src={AddressFormDivider}
                    alt='Address Form Divider'
                    aria-hidden='true'
                  />
                </div>
              </div>

              <form onSubmit={handleFormSubmit} aria-labelledby='dialog-title' noValidate>
                <AddressForm
                  ref={formRef}
                  initialAddress={initialAddress}
                  initialMakeDefault={false}
                />

                <div className={style.DialogActions}>
                  <PrimaryButton
                    size='lg'
                    className={style.CancelButton}
                    onPress={() => {
                      handleClose(close)
                    }}
                    aria-label='Cancel'
                    isDisabled={mutation.isPending}
                  >
                    Cancel
                  </PrimaryButton>
                  <PrimaryButton
                    size='lg'
                    type='submit'
                    aria-label='Save address'
                    isDisabled={mutation.isPending}
                  >
                    {mutation.isPending ? 'Saving...' : 'Save'}
                  </PrimaryButton>
                </div>
              </form>
            </>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
})
